import './TagsEditModal.scss';
import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { useContext } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { SA_updatePublicAssetAPI, SA_updateCompanyFileAPI } from '../../../../../Services/SA_api';

export const TagsEditModal = ({ isOpen, onClose, fileId, tags = [], onTagsUpdated, companyId }) => {
    const { i18n } = useContext(I18nReactContext);
    const [tagsInput, setTagsInput] = useState(tags.join(', '));
    const [isLoading, setIsLoading] = useState(false);

    // Log props when modal opens
    React.useEffect(() => {
        if (isOpen) {
            console.log('TagsEditModal - opened with props:', { fileId, companyId, tagsCount: tags?.length });
        }
    }, [isOpen, fileId, companyId, tags]);

    // Reset tags input when modal opens with new tags
    React.useEffect(() => {
        setTagsInput(tags.join(', '));
    }, [tags, isOpen]);

    const handleInputChange = (event) => {
        setTagsInput(event.target.value);
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            // Parse tags from comma-separated string
            const newTags = tagsInput
                .split(',')
                .map(tag => tag.trim())
                .filter(tag => tag); // Remove empty tags

            // Determine if this is a public asset or a company file
            // If companyId is 'none' or not provided, it's a public asset
            // Convert companyId to string for comparison to ensure correct matching
            const isPublicAsset = !companyId || String(companyId) === 'none';

            console.log('TagsEditModal - handleSave', { 
                fileId, 
                companyId, 
                isPublicAsset,
                apiToCall: isPublicAsset ? 'SA_updatePublicAssetAPI' : 'SA_updateCompanyFileAPI' 
            });

            try {
                if (isPublicAsset) {
                    // Update public asset tags via API
                    console.log('Calling SA_updatePublicAssetAPI with:', { publicAssetId: fileId, tags: newTags });
                    await SA_updatePublicAssetAPI({
                        publicAssetId: fileId,
                        tags: newTags
                    });
                    console.log('SA_updatePublicAssetAPI call successful');
                } else {
                    // Update company file tags via API
                    console.log('Calling SA_updateCompanyFileAPI with:', { companyFileId: fileId, tags: newTags });
                    await SA_updateCompanyFileAPI({
                        companyFileId: fileId,
                        tags: newTags
                    });
                    console.log('SA_updateCompanyFileAPI call successful');
                }
            } catch (apiError) {
                console.error('API call error:', apiError);
                throw apiError;
            }

            // Notify parent component that tags were updated
            if (onTagsUpdated) {
                onTagsUpdated(newTags);
            }

            onClose();
        } catch (error) {
            console.error('Error updating tags:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog 
            open={isOpen} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {i18n.translate('superAdmin.assets.tags.editTags')}
            </DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    value={tagsInput}
                    onChange={handleInputChange}
                    placeholder={i18n.translate('superAdmin.assets.tags.enterTagsPlaceholder')}
                    disabled={isLoading}
                    multiline
                    rows={4}
                    helperText={i18n.translate('superAdmin.assets.tags.tagsHelperText')}
                />
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={onClose} 
                    disabled={isLoading}
                    color="primary"
                >
                    {i18n.translate('superAdmin.assets.tags.cancel')}
                </Button>
                <Button 
                    onClick={handleSave} 
                    disabled={isLoading}
                    color="primary"
                    variant="contained"
                >
                    {i18n.translate('superAdmin.assets.tags.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
